/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import { ArrowDownLeftIcon, ArrowUpRightIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { ReactElement } from 'react'
import { Tooltip } from '../Tooltip'
import { formatTimestamp } from 'app/date.utils'
import type { CommContext } from 'app/hooks/usePipelineCommContext'
import { lastCommIcon, lastCommLabel, lastCommTypeLabel } from 'lib/lastCommMedium'

/**
 * The card's dedicated Communications band (bottom of the card). Shows the last
 * contact (direction + type + relative time) and an unread badge, from the
 * pre-fetched comm context. Rendered whenever `showCommunications` is on.
 */
export function CommunicationsSection({
  commContext,
  compact,
  className,
}: {
  commContext?: CommContext
  compact: boolean
  className?: string
}): ReactElement {
  const comm = commContext?.lastComm
  const hasUnread = commContext?.hasUnread ?? false
  const inbound = comm?.direction === `inbound`
  const DirIcon = inbound ? ArrowDownLeftIcon : ArrowUpRightIcon
  const TypeIcon = lastCommIcon(comm?.type)

  return (
    <div
      className={classNames(
        `flex items-center justify-between gap-x-2 bg-surface-muted px-3`,
        compact ? `py-1.5` : `py-2`,
        className,
      )}
    >
      {comm ? (
        <span
          role="group"
          aria-label={lastCommLabel(comm)}
          className="inline-flex items-center gap-x-1 text-[11px] font-medium text-subtle"
        >
          {comm.direction && <DirIcon aria-hidden="true" className="h-3.5 w-3.5" />}
          {TypeIcon && <TypeIcon aria-hidden="true" className="h-3.5 w-3.5" />}
          {lastCommTypeLabel(comm.type) ?? `Last communication`} · {formatTimestamp(comm.date)}
        </span>
      ) : (
        <span className="text-[11px] text-minimal">No communication</span>
      )}
      {hasUnread && (
        <Tooltip message="Unread messages" placement="top">
          <span className="flex items-center gap-x-1 text-[10px] font-semibold uppercase tracking-wide text-red-600">
            <span className="h-2 w-2 rounded-full bg-red-600" />
            Unread
          </span>
        </Tooltip>
      )}
    </div>
  )
}
